import React, { useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Pencil, X, ChevronDown } from 'lucide-react';
import { message } from 'antd';
import { V, cardStyle, cardHoverStyle, h2Style } from '../vividGlass';
import brandDnaActions from '../../../../actions/brandDna';
import { PILLAR_CONFIG } from './pillarConfig';

// ── Scale display ─────────────────────────────────────────────
function ScaleDisplay({ value }) {
  const v = Math.min(10, Math.max(0, Number(value) || 0));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ display: 'flex', gap: 3 }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} style={{
            width: 16, height: 6, borderRadius: 3,
            background: i < v ? V.primary : 'rgba(255,255,255,0.1)',
          }} />
        ))}
      </div>
      <span style={{ fontSize: 12, color: V.textMuted, fontWeight: 700, fontFamily: V.font }}>{v}/10</span>
    </div>
  );
}

// ── Color swatch ──────────────────────────────────────────────
function ColorSwatch({ hex, role }) {
  if (!hex) return <span style={{ color: V.textMuted, fontSize: 13, fontStyle: 'italic' }}>Not set</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 24, height: 24, borderRadius: 6, background: hex, border: `1px solid rgba(255,255,255,0.2)`, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: V.textSub, fontFamily: 'monospace' }}>{hex}</span>
      {role && <span style={{ fontSize: 11, color: V.textMuted, fontFamily: V.font }}>{role}</span>}
    </div>
  );
}

// ── Tag list (display only) ───────────────────────────────────
function TagList({ tags }) {
  if (!tags?.length) return <span style={{ color: V.textMuted, fontSize: 13, fontStyle: 'italic' }}>Not set</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {tags.map((t, i) => (
        <span key={i} style={{
          padding: '4px 11px', borderRadius: 99,
          background: 'rgba(255,255,255,0.07)', border: `1px solid ${V.glassBorder}`,
          fontSize: 12, color: V.textSub, fontFamily: V.font,
        }}>
          {t}
        </span>
      ))}
    </div>
  );
}

// ── Inline editor ─────────────────────────────────────────────
function FieldEditor({ field, initialValue, onSave, onCancel }) {
  const [val, setVal] = useState(initialValue);
  const [tagInput, setTagInput] = useState('');
  const [editingTagIdx, setEditingTagIdx] = useState(null);
  const tagEditRef = useRef(null);

  const inputBase = {
    padding: '7px 10px', borderRadius: 10, outline: 'none',
    background: 'rgba(255,255,255,0.07)', border: `1px solid rgba(255,255,255,0.18)`,
    color: V.text, fontSize: 13, fontFamily: V.font,
  };

  const handleTagAdd = () => {
    const t = tagInput.trim();
    if (t && !val.includes(t)) setVal([...val, t]);
    setTagInput('');
  };
  const handleTagRemove = (idx) => setVal(val.filter((_, i) => i !== idx));
  const handleTagEdit = (idx, newText) => {
    const updated = val.map((t, i) => i === idx ? newText : t);
    setVal(updated);
  };
  const commitTagEdit = (idx) => {
    const trimmed = (val[idx] || '').trim();
    if (!trimmed) handleTagRemove(idx);
    else handleTagEdit(idx, trimmed);
    setEditingTagIdx(null);
  };

  if (field.type === 'scale') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input type="range" min={0} max={10} value={Number(val) || 0}
          onChange={(e) => setVal(Number(e.target.value))}
          style={{ flex: 1, accentColor: V.primary }} />
        <span style={{ fontSize: 13, color: V.textSub, fontWeight: 700, minWidth: 32, fontFamily: V.font }}>{val ?? 0}/10</span>
        <SaveCancel onSave={() => onSave(val)} onCancel={onCancel} />
      </div>
    );
  }

  if (field.type === 'color' || field.type === 'color_pair') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input type="color" value={val?.hex || val || '#000000'}
          onChange={(e) => setVal(field.type === 'color_pair' ? { ...val, hex: e.target.value } : e.target.value)}
          style={{ width: 38, height: 38, border: 'none', background: 'none', cursor: 'pointer', padding: 2, borderRadius: 8 }} />
        {field.type === 'color_pair' && (
          <input value={val?.role || ''} onChange={(e) => setVal({ ...val, role: e.target.value })}
            placeholder="Role (e.g. Background)"
            style={{ ...inputBase, flex: 1, minWidth: 120 }} />
        )}
        <SaveCancel onSave={() => onSave(field.type === 'color_pair' ? val : (val?.hex || val))} onCancel={onCancel} />
      </div>
    );
  }

  if (field.type === 'array') {
    return (
      <div>
        {/* Tags — click to edit, X to remove */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {(val || []).map((tag, i) => (
            editingTagIdx === i ? (
              <input
                key={i}
                ref={tagEditRef}
                autoFocus
                value={tag}
                onChange={(e) => handleTagEdit(i, e.target.value)}
                onBlur={() => commitTagEdit(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitTagEdit(i); }
                  if (e.key === 'Escape') { setEditingTagIdx(null); }
                }}
                style={{ ...inputBase, padding: '3px 8px', borderRadius: 99, fontSize: 12, width: Math.max(80, tag.length * 8) }}
              />
            ) : (
              <span key={i} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 99,
                background: 'rgba(255,126,103,0.12)', border: '1px solid rgba(255,126,103,0.3)',
                fontSize: 12, color: V.text, cursor: 'pointer', fontFamily: V.font,
              }}>
                <span onClick={() => setEditingTagIdx(i)} title="Click to edit" style={{ cursor: 'text' }}>{tag}</span>
                <X size={10} style={{ cursor: 'pointer', opacity: 0.6, flexShrink: 0 }} onClick={() => handleTagRemove(i)} />
              </span>
            )
          ))}
        </div>
        {/* Add new tag */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleTagAdd(); } }}
            placeholder="Press Enter to add"
            style={{ ...inputBase, flex: 1 }}
          />
          <SaveCancel onSave={() => onSave(val)} onCancel={onCancel} />
        </div>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', display: 'block', marginTop: 4, fontFamily: V.font }}>
          Tip: click any tag to edit it
        </span>
      </div>
    );
  }

  // text or textarea
  const isLong = field.type === 'textarea';
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
      {isLong ? (
        <textarea
          autoFocus value={val || ''} onChange={(e) => setVal(e.target.value)} rows={3}
          style={{ ...inputBase, flex: 1, resize: 'vertical' }}
        />
      ) : (
        <input
          autoFocus value={val || ''} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSave(val); } if (e.key === 'Escape') onCancel(); }}
          style={{ ...inputBase, flex: 1 }}
        />
      )}
      <SaveCancel onSave={() => onSave(val)} onCancel={onCancel} />
    </div>
  );
}

function SaveCancel({ onSave, onCancel }) {
  return (
    <div style={{ display: 'flex', gap: 12, flexShrink: 0, alignItems: 'center' }}>
      <button type="button" onClick={onSave}
        style={{ background: 'none', border: 'none', color: '#4ade80', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: V.font, padding: '4px 0' }}>
        Save
      </button>
      <button type="button" onClick={onCancel}
        style={{ background: 'none', border: 'none', color: V.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: V.font, padding: '4px 0' }}>
        Cancel
      </button>
    </div>
  );
}

// ── Field row ─────────────────────────────────────────────────
function FieldRow({ field, data, brand, pillarKey, brandId, onSaved, isOwner }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // brand-sourced fields read from brand object, not pillar data
  const rawValue = field.source === 'brand' ? brand?.[field.key] : data?.[field.key];
  const isEmpty = rawValue === null || rawValue === undefined || rawValue === ''
    || (Array.isArray(rawValue) && rawValue.length === 0);

  const fieldLabelStyle = {
    fontSize: 10, fontWeight: 900, color: V.textMuted,
    textTransform: 'uppercase', letterSpacing: '1.5px',
    display: 'block', marginBottom: 5, fontFamily: V.font,
  };

  const getEditInitial = () => {
    if (field.type === 'color_pair') return { hex: rawValue || '#000000', role: data?.[field.roleKey] || '' };
    if (field.type === 'array') return Array.isArray(rawValue) ? [...rawValue] : [];
    return rawValue ?? '';
  };

  const handleSave = useCallback(async (newVal) => {
    setSaving(true);
    try {
      if (field.source === 'brand') {
        await brandDnaActions.updateBrand(brandId, { [field.key]: newVal });
      } else {
        const updates = { [field.key]: newVal };
        if (field.type === 'color_pair') {
          updates[field.key] = newVal?.hex;
          updates[field.roleKey] = newVal?.role;
        }
        await brandDnaActions.updatePillar(brandId, pillarKey, updates);
      }
      message.success('Saved');
      setEditing(false);
      onSaved();
    } catch {
      message.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [brandId, field, pillarKey, onSaved]);

  const renderValue = () => {
    if (isEmpty) return (
      <span style={{ color: V.textMuted, fontSize: 13, fontStyle: 'italic', fontFamily: V.font }}>
        {isOwner ? 'Click pencil to add' : 'Not set'}
      </span>
    );
    if (field.type === 'scale') return <ScaleDisplay value={rawValue} />;
    if (field.type === 'color') return <ColorSwatch hex={rawValue} />;
    if (field.type === 'color_pair') return <ColorSwatch hex={rawValue} role={data?.[field.roleKey]} />;
    if (field.type === 'array') return <TagList tags={rawValue} />;
    return <span style={{ fontSize: 14, color: V.textSub, fontFamily: V.font, lineHeight: 1.5 }}>{rawValue}</span>;
  };

  return (
    <div style={{ padding: '12px 0', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={fieldLabelStyle}>{field.label}</span>
            {field.source === 'brand' && (
              <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: V.info, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
                Brand
              </span>
            )}
          </div>
          {editing ? (
            <FieldEditor field={field} initialValue={getEditInitial()} onSave={handleSave} onCancel={() => setEditing(false)} />
          ) : (
            renderValue()
          )}
          {field.hint && !editing && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', display: 'block', marginTop: 4, fontFamily: V.font }}>
              {field.hint}
            </span>
          )}
        </div>
        {isOwner && !editing && !saving && (
          <button type="button"
            onClick={() => setEditing(true)}
            style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: V.textMuted, cursor: 'pointer', flexShrink: 0 }}>
            <Pencil size={12} />
          </button>
        )}
        {saving && <span style={{ fontSize: 11, color: V.textMuted, paddingTop: 6, fontFamily: V.font }}>Saving…</span>}
      </div>
    </div>
  );
}

// ── Main PillarCard ───────────────────────────────────────────
export default function PillarCard({ pillarKey, brand, brandId, onRefresh }) {
  const config = PILLAR_CONFIG[pillarKey];
  const data = brand?.[pillarKey];
  const isOwner = brand?.is_owner;
  const [openGroups, setOpenGroups] = useState(() => config.groups.map(() => true));
  const [hovered, setHovered] = useState(false);

  const toggleGroup = (i) => setOpenGroups((prev) => prev.map((v, idx) => idx === i ? !v : v));
  const Icon = config.Icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...cardStyle, ...(hovered ? cardHoverStyle : {}), marginBottom: 0 }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid rgba(255,255,255,0.07)` }}>
        <div style={{ width: 38, height: 38, borderRadius: 12, background: `${config.accent}18`, border: `1px solid ${config.accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={17} color={config.accent} />
        </div>
        <div>
          <h2 style={{ ...h2Style, marginBottom: 2 }}>{config.label}</h2>
          <span style={{ fontSize: 11, color: V.textMuted, fontFamily: V.font }}>
            {config.groups.reduce((sum, g) => sum + g.fields.length, 0)} fields
          </span>
        </div>
      </div>

      {/* Groups */}
      {config.groups.map((group, gi) => (
        <div key={gi} style={{ marginBottom: gi < config.groups.length - 1 ? 4 : 0 }}>
          {/* Group header */}
          <button
            type="button"
            onClick={() => toggleGroup(gi)}
            style={{
              width: '100%', background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 0', paddingLeft: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: config.accent, flexShrink: 0 }} />
              <span style={{ fontSize: 10, fontWeight: 900, color: config.accent, textTransform: 'uppercase', letterSpacing: '1.5px', fontFamily: V.font }}>
                {group.title}
              </span>
            </div>
            <span style={{ color: V.textMuted, display: 'flex', transform: openGroups[gi] ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}>
              <ChevronDown size={14} />
            </span>
          </button>

          {openGroups[gi] && (
            <div style={{ paddingLeft: 11, borderLeft: `2px solid ${config.accent}20` }}>
              {group.fields.map((field) => (
                <FieldRow
                  key={field.key}
                  field={field}
                  data={data}
                  brand={brand}
                  pillarKey={pillarKey}
                  brandId={brandId}
                  onSaved={onRefresh}
                  isOwner={isOwner}
                />
              ))}
            </div>
          )}

          {/* Separator between groups */}
          {gi < config.groups.length - 1 && (
            <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '4px 0 8px' }} />
          )}
        </div>
      ))}
    </motion.div>
  );
}
